import React, { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Clouds, Cloud, Stars } from '@react-three/drei'
import * as THREE from 'three'

// Weather conditions
export interface WeatherCondition {
  name: string
  description: string
  cloudCoverage: number // 0-1
  cloudOpacity: number // 0-1
  cloudSpeed: number // movement speed
  precipitation: 'none' | 'light_rain' | 'heavy_rain' | 'snow'
  windStrength: number // 0-1
  visibility: number // 0-1 (affects fog)
  atmosphericPressure: number // affects sky color
}

export const WEATHER_CONDITIONS: Record<string, WeatherCondition> = {
  clear: {
    name: 'Clear Sky',
    description: 'Perfect clear weather with no clouds',
    cloudCoverage: 0.0,
    cloudOpacity: 0.0,
    cloudSpeed: 0.1,
    precipitation: 'none',
    windStrength: 0.2,
    visibility: 1.0,
    atmosphericPressure: 1.0,
  },
  partly_cloudy: {
    name: 'Partly Cloudy',
    description: 'Scattered clouds with good visibility',
    cloudCoverage: 0.3,
    cloudOpacity: 0.6,
    cloudSpeed: 0.3,
    precipitation: 'none',
    windStrength: 0.4,
    visibility: 0.9,
    atmosphericPressure: 0.95,
  },
  overcast: {
    name: 'Overcast',
    description: 'Heavy cloud cover with diffused lighting',
    cloudCoverage: 0.8,
    cloudOpacity: 0.9,
    cloudSpeed: 0.2,
    precipitation: 'none',
    windStrength: 0.3,
    visibility: 0.7,
    atmosphericPressure: 0.85,
  },
  light_rain: {
    name: 'Light Rain',
    description: 'Gentle rainfall with moderate clouds',
    cloudCoverage: 0.7,
    cloudOpacity: 0.8,
    cloudSpeed: 0.4,
    precipitation: 'light_rain',
    windStrength: 0.5,
    visibility: 0.6,
    atmosphericPressure: 0.8,
  },
  heavy_rain: {
    name: 'Heavy Rain',
    description: 'Intense rainfall with dark storm clouds',
    cloudCoverage: 0.95,
    cloudOpacity: 1.0,
    cloudSpeed: 0.6,
    precipitation: 'heavy_rain',
    windStrength: 0.8,
    visibility: 0.4,
    atmosphericPressure: 0.7,
  },
  snow: {
    name: 'Snow',
    description: 'Gentle snowfall with winter atmosphere',
    cloudCoverage: 0.6,
    cloudOpacity: 0.7,
    cloudSpeed: 0.2,
    precipitation: 'snow',
    windStrength: 0.3,
    visibility: 0.5,
    atmosphericPressure: 0.9,
  },
}

// Seasonal variations
export interface SeasonConfig {
  name: string
  foliageColor: string
  grassColor: string
  skyTint: string
  sunIntensityMultiplier: number
  ambientTemperature: number // affects material appearance
  dayLength: number // hours of daylight
}

export const SEASONS: Record<string, SeasonConfig> = {
  spring: {
    name: 'Spring',
    foliageColor: '#90EE90',
    grassColor: '#7CFC00',
    skyTint: '#87CEEB',
    sunIntensityMultiplier: 1.0,
    ambientTemperature: 18, // Celsius
    dayLength: 12,
  },
  summer: {
    name: 'Summer',
    foliageColor: '#228B22',
    grassColor: '#32CD32',
    skyTint: '#4169E1',
    sunIntensityMultiplier: 1.2,
    ambientTemperature: 28,
    dayLength: 14,
  },
  autumn: {
    name: 'Autumn',
    foliageColor: '#FF6347',
    grassColor: '#DAA520',
    skyTint: '#B0C4DE',
    sunIntensityMultiplier: 0.9,
    ambientTemperature: 15,
    dayLength: 10,
  },
  winter: {
    name: 'Winter',
    foliageColor: '#8B4513',
    grassColor: '#F5F5DC',
    skyTint: '#708090',
    sunIntensityMultiplier: 0.7,
    ambientTemperature: 2,
    dayLength: 8,
  },
}

interface EnvironmentSystemProps {
  weather: string
  season: string
  enableWeatherEffects?: boolean
  enableSeasonalChanges?: boolean
  enableAtmosphericEffects?: boolean
  timeOfDay?: number
}

export const EnvironmentSystem: React.FC<EnvironmentSystemProps> = ({
  weather = 'clear',
  season = 'summer',
  enableWeatherEffects = true,
  enableSeasonalChanges = true,
  enableAtmosphericEffects = true,
  timeOfDay = 12,
}) => {
  const { scene } = useThree()
  const cloudsRef = useRef<THREE.Group>(null)
  const precipitationRef = useRef<THREE.Points>(null)
  
  const weatherConfig = WEATHER_CONDITIONS[weather] || WEATHER_CONDITIONS.clear
  const seasonConfig = SEASONS[season] || SEASONS.summer

  // Animate clouds
  useFrame((state, delta) => {
    if (cloudsRef.current && enableWeatherEffects) {
      cloudsRef.current.position.x += weatherConfig.cloudSpeed * delta * 2
      
      // Reset cloud position for infinite loop
      if (cloudsRef.current.position.x > 100) {
        cloudsRef.current.position.x = -100
      }
    }
  })

  // Create precipitation particles
  useEffect(() => {
    if (!enableWeatherEffects || weatherConfig.precipitation === 'none') return

    const particleCount = weatherConfig.precipitation === 'heavy_rain' ? 2000 : 1000
    const geometry = new THREE.BufferGeometry()
    const positions = new Float32Array(particleCount * 3)
    const velocities = new Float32Array(particleCount * 3)

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3
      
      // Random positions in a large area above the scene
      positions[i3] = (Math.random() - 0.5) * 200
      positions[i3 + 1] = Math.random() * 50 + 50
      positions[i3 + 2] = (Math.random() - 0.5) * 200

      // Velocities for different precipitation types
      if (weatherConfig.precipitation === 'snow') {
        velocities[i3] = (Math.random() - 0.5) * 0.5
        velocities[i3 + 1] = -Math.random() * 2 - 1
        velocities[i3 + 2] = (Math.random() - 0.5) * 0.5
      } else {
        // Rain
        velocities[i3] = weatherConfig.windStrength * (Math.random() - 0.5) * 2
        velocities[i3 + 1] = -Math.random() * 10 - 5
        velocities[i3 + 2] = (Math.random() - 0.5) * 1
      }
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('velocity', new THREE.BufferAttribute(velocities, 3))

    return () => {
      geometry.dispose()
    }
  }, [weatherConfig, enableWeatherEffects])

  // Update scene background based on season and weather
  useEffect(() => {
    if (enableSeasonalChanges || enableWeatherEffects) {
      const baseColor = new THREE.Color(seasonConfig.skyTint)
      
      // Adjust for weather conditions
      if (weatherConfig.cloudCoverage > 0.5) {
        baseColor.lerp(new THREE.Color('#808080'), weatherConfig.cloudCoverage * 0.5)
      }
      
      // Adjust for time of day
      if (timeOfDay < 6 || timeOfDay > 20) {
        baseColor.lerp(new THREE.Color('#191970'), 0.7) // Night blue
      } else if (timeOfDay < 8 || timeOfDay > 18) {
        baseColor.lerp(new THREE.Color('#FF6347'), 0.3) // Golden hour
      }
      
      scene.background = baseColor
    }
  }, [scene, seasonConfig, weatherConfig, timeOfDay, enableSeasonalChanges, enableWeatherEffects])

  return (
    <>
      {/* Clouds */}
      {enableWeatherEffects && weatherConfig.cloudCoverage > 0 && (
        <group ref={cloudsRef}>
          <Clouds material={THREE.MeshLambertMaterial}>
            <Cloud
              opacity={weatherConfig.cloudOpacity}
              speed={weatherConfig.cloudSpeed}
              color={weatherConfig.precipitation !== 'none' ? '#606060' : '#FFFFFF'}
            />
            <Cloud
              opacity={weatherConfig.cloudOpacity * 0.8}
              speed={weatherConfig.cloudSpeed * 0.8}
              position={[30, 5, -20]}
              color={weatherConfig.precipitation !== 'none' ? '#707070' : '#F5F5F5'}
            />
            <Cloud
              opacity={weatherConfig.cloudOpacity * 0.6}
              speed={weatherConfig.cloudSpeed * 1.2}
              position={[-40, 8, 10]}
              color={weatherConfig.precipitation !== 'none' ? '#505050' : '#FAFAFA'}
            />
          </Clouds>
        </group>
      )}

      {/* Stars for night time */}
      {timeOfDay < 6 || timeOfDay > 20 ? (
        <Stars
          radius={300}
          depth={60}
          count={5000}
          factor={4}
          saturation={0}
          fade
          speed={0.5}
        />
      ) : null}

      {/* Atmospheric Particles (dust, pollen, etc.) */}
      {enableAtmosphericEffects && (
        <points>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={200}
              array={new Float32Array(
                Array.from({ length: 600 }, () => (Math.random() - 0.5) * 100)
              )}
              itemSize={3}
            />
          </bufferGeometry>
          <pointsMaterial
            size={0.1}
            color={seasonConfig.skyTint}
            transparent
            opacity={0.3}
            sizeAttenuation
          />
        </points>
      )}

      {/* Ground Fog Effect */}
      {enableAtmosphericEffects && weatherConfig.visibility < 0.8 && (
        <mesh position={[0, 0.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[200, 200, 32, 32]} />
          <meshBasicMaterial
            color="#FFFFFF"
            transparent
            opacity={0.1 * (1 - weatherConfig.visibility)}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
    </>
  )
}

// Hook for getting environment effects
export const useEnvironmentEffects = (weather: string, season: string, timeOfDay: number) => {
  const weatherConfig = WEATHER_CONDITIONS[weather] || WEATHER_CONDITIONS.clear
  const seasonConfig = SEASONS[season] || SEASONS.summer

  return {
    weatherConfig,
    seasonConfig,
    isNight: timeOfDay < 6 || timeOfDay > 20,
    isGoldenHour: (timeOfDay >= 6 && timeOfDay <= 8) || (timeOfDay >= 18 && timeOfDay <= 20),
    isDaytime: timeOfDay >= 8 && timeOfDay <= 18,
    precipitationActive: weatherConfig.precipitation !== 'none',
    visibility: weatherConfig.visibility,
    atmosphericDensity: 1 - weatherConfig.visibility,
  }
}

// Weather transition utilities
export const getWeatherTransition = (fromWeather: string, toWeather: string, progress: number) => {
  const from = WEATHER_CONDITIONS[fromWeather] || WEATHER_CONDITIONS.clear
  const to = WEATHER_CONDITIONS[toWeather] || WEATHER_CONDITIONS.clear

  return {
    cloudCoverage: from.cloudCoverage + (to.cloudCoverage - from.cloudCoverage) * progress,
    cloudOpacity: from.cloudOpacity + (to.cloudOpacity - from.cloudOpacity) * progress,
    visibility: from.visibility + (to.visibility - from.visibility) * progress,
    windStrength: from.windStrength + (to.windStrength - from.windStrength) * progress,
  }
}

export default EnvironmentSystem
